import client from './axios';

import {
  AddressFromIpRes,
  AddressListRes,
  ATOME_USEABLE_CURRENCIES,
  BANK_TRANSFER_ACCOUNT,
  BankInfo,
  BillingInfoPayload,
  CountryCode,
  Currency,
  CustomMessage,
  CustomMessageInit,
  ExchangeRate,
  IKlarnaAuthorizeRes,
  KLARNA_CURRENCY_COUNTRIES,
  KlarnaCurrency,
  Money,
  OrderProps,
  OrderRespDto,
  OrderType,
  PaymentInitKlarna,
  PaymentInitReq,
  PaymentMethodParams,
  PaymentRespDto,
  PayMethods,
  PaypalDeductionsResp,
  RefreshPaymentStatusResponse,
  SubscribePricePayload,
  SubscribeVersion,
  SubscriptionOptions,
} from '@/api/types/payment';
import ecommerce from '@/request/ecommerce';
import { Config, http } from '@/request/http';
import { WK_GATEWAY_URL } from '@/utils/env';
import { filterKeysMap } from '@/utils/payment';

// eslint-disable-next-line import/no-cycle

/**
 * 获取订单信息
 * @param uuid 订单id
 * @returns 订单信息
 */
export const getOrderInfo = async (
  uuid: string,
  noAuth = false,
): Promise<OrderProps> => {
  if (noAuth) {
    const res = (await http(`public/api/ec/order/${uuid}`)) as OrderProps;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    res.money = res?.actualPaymentAmount;
    return res;
  }

  const res = (await client.get(
    `api/student/order/detail/full/${uuid}`,
  )) as OrderProps;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  res.money = res?.actualPaymentAmount;
  return res;
};

/**
 * 订阅订单
 * @param uuid
 */
export const getSubscriptionOrderInfo = async (
  uuid: string,
  noAuth = false,
): Promise<OrderProps> => {
  if (noAuth) {
    const res = (await http(
      `public/api/subscription/${uuid}/deduction`,
    )) as OrderProps;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    res.money = res?.actualPaymentAmount;
    return res;
  }

  const res = (await client.get(
    `api/student/order/subscribe/${uuid}/deduction`,
  )) as OrderProps;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  res.money = res?.actualPaymentAmount;
  return res;
};

/**
 * 获取订单信息
 * @param uuid 订单主键
 * @returns 订单信息
 */
export const getOrder = (uuid: string): Promise<OrderRespDto> => {
  return client.get(`api/student/order/detail/full/${uuid}`);
};

/**
 * 用过paymentId获取订单信息
 * @param uuid 订单主键
 * @param paymenyId 支付id
 * @returns 订单信息
 */
export const getOrderByPaymenyId = (
  uuid: string,
  paymentId: string,
  noAuth = false,
): Promise<OrderRespDto> => {
  if (noAuth) {
    return http(`public/api/ec/order/${uuid}/payment/${paymentId}`);
  }
  return client.get(`api/student/order/${uuid}/payment/${paymentId}`);
};
/**
 * 修改订单状态为已支付待确认
 * @param uuid 支付主键
 * @returns 无返回
 */
export const handleOrderChangeStatus = (uuid: string): Promise<void> => {
  if (!uuid) {
    return Promise.reject(new Error('orderId is required'));
  }
  return http(`public/api/payment/${uuid}/confirm`, {
    method: 'PUT',
    toJson: false,
  });
};

/**
 * paypal发起扣款
 * @param token 用户对该笔支付的授权码
 * @returns 扣款信息
 */
export const handlePaypalDeductions = (
  token: string,
): Promise<PaypalDeductionsResp> => {
  return http<PaypalDeductionsResp>(
    `public/api/payment/paypal/capture/${token}`,
    {
      method: 'PUT',
      errorToast: false,
    },
  ).catch(res => {
    const { message } = res;
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      message: message && JSON.parse(message),
    } as PaypalDeductionsResp);
  });
};

/**
 * 获取支付信息
 * @param uuid 支付主键
 * @returns 支付信息
 */
export const getPayment = (uuid: string): Promise<PaymentRespDto> => {
  return http(`public/api/payment/${uuid}`);
};

/**
 * 支付初始化信息
 * @param params 支付初始化参数
 * @returns 订单信息
 */
export const paymentInit = <T = CustomMessageInit, R = CustomMessage>(
  params: PaymentInitReq<T>,
): Promise<OrderRespDto<R>> => {
  return http('public/api/payment/init', { data: params, method: 'POST' });
};

/**
 * 获取配置信息
 * @param params
 */
export const subscriptionPaymentInit = (
  subscriptionId: string,
  params: any,
): Promise<any> => {
  return http(`public/api/subscription/${subscriptionId}/authorization`, {
    data: params,
    method: 'POST',
  });
};

export interface SubscriptionAuthOkResp {
  /**
   * 订阅id
   */
  uuid: string;
  /**
   * 订阅状态
   */
  status: 'PENDING_AUTHORIZED' | 'OK' | 'DEDUCTION_FAILED' | 'CANCELLED';
  /**
   * 支付信息
   */
  payment: PaymentRespDto;
}
/**
 * 授权成功后的更新接口
 * @param params
 */
export const updatePaymentInit = (
  subscriptionId: string,
  params: any,
): Promise<SubscriptionAuthOkResp> => {
  return http(`public/api/subscription/${subscriptionId}/authorization`, {
    data: params,
    method: 'PUT',
  });
};

/**
 * CITCON订阅初始化
 * @param subscriptionId 订阅id
 * @param param1 订阅初始化参数
 * @returns 授权地址
 */
export const citconSubscriptionInit = async (
  subscriptionId: string,
  paymentMethodParams: PaymentMethodParams,
): Promise<string> => {
  const { redirectURL } = await subscriptionPaymentInit(subscriptionId, {
    paymentMethod: 'CITCON',
    paymentMethodParams,
  });
  return redirectURL;
};

/**
 * 授权成功调用接口
 * @param subscriptionId 订阅id
 * @param param1 授权成功参数
 * @returns 扣款结果
 */
export const citconPaypalSubscriptionAuthOk = async (
  subscriptionId: string,
  { reference, countryCode }: { reference: string; countryCode?: string },
) => {
  return updatePaymentInit(subscriptionId, {
    reference,
    countryCode,
  });
};

/**
 * 更新支付方式-获取配置信息
 */
export const getUpdateSubscriptionPaymentInit = <T>(
  subscriptionId: string,
  params: any,
): Promise<T> => {
  return http(
    `public/api/subscription/${subscriptionId}/authorization/init-update-param`,
    {
      data: params,
      method: 'POST',
    },
  );
};

/**
 * 初始化订阅更新支付
 * @param subscriptionId 订阅id
 * @returns paypal授权页面url
 */
export const initUpdateCitconPaypalSubscription = async (
  subscriptionId: string,
  paymentMethodParams: PaymentMethodParams,
) => {
  const { redirectURL } = await getUpdateSubscriptionPaymentInit<{
    redirectURL: string;
  }>(subscriptionId, {
    paymentMethod: 'CITCON',
    paymentMethodParams,
  });
  return redirectURL as string;
};

/**
 * 更新支付方式-授权成功后的更新接口
 */
export const updateCitconPaypalSubscriptionOk = (
  subscriptionId: string,
  reference: string,
) => {
  return http(
    `public/api/subscription/${subscriptionId}/authorization/CITCON/update-ok`,
    {
      data: { reference },
      method: 'PUT',
      toJson: false,
    },
  );
};

/**
 * 更新支付方式-授权成功后的更新接口
 */
export const updateAccessPaymentInit = (
  subscriptionId: string,
  params: any,
) => {
  return http(
    `public/api/subscription/${subscriptionId}/authorization/AIRWALLEX/update-ok`,
    {
      data: params,
      method: 'PUT',
      toJson: false,
    },
  );
};

// klarna支付
export const handlePaymentKlarna = (params: PaymentInitKlarna) => {
  return ecommerce.post<void>(
    '/public/callback/klarna',
    {
      ...params,
    },
    {
      skipTransformResponse: true,
    },
  );
};

export interface AirwallexWechatPayResultPollingRes {
  status: 'REQUIRES_CUSTOMER_ACTION' | 'SUCCEEDED' | 'CANCELLED';
  payment: {
    uuid: string;
  };
}

export const airwallexWechatPayResultPolling = (
  orderId: string,
  paymentMethod: PayMethods,
) => {
  return ecommerce.post<
    AirwallexWechatPayResultPollingRes,
    AirwallexWechatPayResultPollingRes
  >(
    `/public/api/payment/query_payment`,
    {
      orderId,
      paymentMethod,
    },
    {
      skipTransformResponse: true,
    },
  );
};

export const requestPublic = <T>(
  endpoint: string,
  { data, token, headers, errorToast = true, ...customConfig }: Config = {},
  params: Record<string, string | number | undefined | null>,
): Promise<T> => {
  const queryParams = Object.entries(params || {})
    .map(([k, v]) => {
      return `${k}=${v}`;
    })
    .join('&');
  const host =
    (process.env.NEXT_PUBLIC_API_WUKONG_ECOMMERCE_URL?.startsWith('http') &&
      process.env.NEXT_PUBLIC_API_WUKONG_ECOMMERCE_URL) ||
    `https:${process.env.NEXT_PUBLIC_API_WUKONG_ECOMMERCE_URL}`;
  return http<T>(`${host}${endpoint}?${queryParams}`, {
    data,
    token,
    headers,
    errorToast,
    ...customConfig,
  });
};

/**
 * 汇率换算
 * @param money 需要换算的金额
 * @param target 目标币种
 * @returns 换算结果
 */
export const exchangeRateConversion = (money: Money, target: Currency) => {
  return requestPublic<ExchangeRate>(
    '/public/api/payment/get_exchange_rate',
    {},
    {
      amount: money.value,
      source: money.currency,
      target: target === 'OTHERS' ? 'USD' : target,
    },
  );
};

/**
 * 多币种汇率换算
 * @param moneys 需要换算汇率的金额
 * @returns 汇率换算结果
 */
export const exchangeRateMultiple = (
  moneys: Array<{ money: Money; target: Currency }>,
): Promise<ExchangeRate[]> => {
  return http('public/api/payment/exchange_rate/_multiple', {
    method: 'POST',
    data: moneys,
  });
};

/**
 * 多币种汇率换算：根据订单号、目标币种获取（提升分期付款汇率，用以弥补换汇损益）
 * @param moneys 需要换算汇率的金额
 * @returns 汇率换算结果
 */
export const getPayAmountByOrderId = (
  orderId: string,
  data: Array<{ method: string; currencies: Currency[] }>,
): Promise<Array<{ method: string; rates: ExchangeRate[] }>> => {
  return http(`public/api/payment/${orderId}/get_pay_amount`, {
    method: 'POST',
    data,
  });
};

/**
 * 获取当前设备ip所在国家代码
 * @returns 国家代码
 */
export const getIpCountryCode = (): Promise<string> => {
  return http('public/ip/country_code', { toJson: false });
};

/**
 * 获取当前系统时间
 * @returns 系统时间
 */
export const getSystemTime = (): Promise<string> => {
  return http('public/ref/sys/date_time/now', { toJson: false });
};

/**
 *
 * @param currency 币种
 * @param exchangeRates 币种汇率列表
 * @returns 该币种可用国家列表
 */
export const getKlarnaAbleCountries = (
  currency: KlarnaCurrency,
  exchangeRates: ExchangeRate[],
) => {
  const amount = exchangeRates.find(v => {
    return v.target.currency === currency;
  })?.target as Money;
  return KLARNA_CURRENCY_COUNTRIES[currency].filter((v: any) => {
    return !v.limit || v.limit.withIn(amount.value);
  });
};

/**
 * 获取匹配该国家代码的国家信息
 * @param currency 币种
 * @param exchangeRates 币种汇率列表
 * @param countryCode 国家代码
 * @returns null or countryInfo
 */
export const getKlarnaAbleCountry = (
  currency: KlarnaCurrency,
  exchangeRates: ExchangeRate[],
  countryCode: string,
) => {
  return getKlarnaAbleCountries(currency, exchangeRates).find((v: any) => {
    return v.value === countryCode;
  });
};

/**
 * 获取该订单klarna可用的币种
 * @param amount 金额
 * @returns 可用币种对应的金额和汇率
 */
export const getKlarnaAvailableCurrency = async (
  amount: Money,
): Promise<ExchangeRate[]> => {
  const exchangeAmounts = await exchangeRateMultiple(
    Object.keys(KLARNA_CURRENCY_COUNTRIES).map(c => {
      return {
        target: c as Currency,
        money: amount,
      };
    }),
  );
  return exchangeAmounts.filter(a => {
    return (
      getKlarnaAbleCountries(
        a.target.currency as KlarnaCurrency,
        exchangeAmounts,
      ).length > 0
    );
  });
};

/**
 * 获取该订单klarna可用的币种：提升分期付款汇率，用以弥补换汇损益
 * 用户选择分期付款时，若订单币种和分期支付币种不同，订单金额变化
 * 分期付款汇率提升2%（仅基础汇率基础上提升，不算之前新增的0.3% ）
 * @param amount 金额
 * @returns 可用币种对应的金额和汇率
 */
export const getInstallmentAvailableCurrencyWithProcedureFee = async (
  orderId: string,
): Promise<{ klarna: ExchangeRate[]; atome: ExchangeRate[] }> => {
  const results = await getPayAmountByOrderId(orderId, [
    {
      method: 'KLARNA',
      currencies: Object.keys(KLARNA_CURRENCY_COUNTRIES) as Currency[],
    },
    {
      method: 'ATOME',
      currencies: ATOME_USEABLE_CURRENCIES.map((c: any) => {
        return c.currency;
      }),
    },
  ]);
  const klarnaCurrencies = results.find(r => {
    return r.method === 'KLARNA';
  })?.rates as ExchangeRate[];
  const atomeCurrencies = results.find(r => {
    return r.method === 'ATOME';
  })?.rates as ExchangeRate[];
  return {
    klarna: klarnaCurrencies.filter(a => {
      return (
        getKlarnaAbleCountries(
          a.target.currency as KlarnaCurrency,
          klarnaCurrencies,
        ).length > 0
      );
    }),
    atome: atomeCurrencies.filter(a => {
      return ATOME_USEABLE_CURRENCIES.find((c: any) => {
        return (
          c.currency === a.target.currency && c.limit.withIn(a.target.value)
        );
      });
    }),
  };
};

/**
 * 获取订单转账支付的费率：+0.3%
 * @param amount 金额
 * @returns 可用币种对应的金额和汇率
 */
export const getBankTransferAvailableCurrencyWithProcedureFee = async (
  orderId: string,
  currency: Currency,
): Promise<ExchangeRate> => {
  const results = await getPayAmountByOrderId(orderId, [
    {
      method: 'REMITTANCE',
      currencies: [currency],
    },
  ]);
  return (
    results.find(r => {
      return r.method === 'REMITTANCE';
    })?.rates as ExchangeRate[]
  )[0];
};

/**
 * klarna授权支付
 * @returns klarna授权结果
 */
export const klarnaAuthorize = async () => {
  if (!window.Klarna) {
    throw new Error('klarna_not_loaded');
  }
  const authorizeRes = await new Promise<IKlarnaAuthorizeRes>(resolve => {
    window.Klarna.Payments.authorize({ auto_finalize: true }, {}, resolve);
  });
  if (!authorizeRes.show_form) {
    throw new Error('form_open_error');
  }
  if (!authorizeRes.approved) {
    throw new Error('user_unapproved');
  }
  return authorizeRes;
};

/**
 * 获取 新西兰地区NZD 转账信息（非PingPong，目前仅余NZD）
 * @param countryCode 国家代码
 * @returns 转账信息
 */
export const bankTransferUseAbleCurrency = (
  activeCurrency: Currency,
  countryCode: string,
): [Currency, BankInfo[]] | undefined => {
  // defend
  const currencyBankInfo = Object.entries(BANK_TRANSFER_ACCOUNT).find(
    ([, v]: any) => {
      if (v.useAbleCountry.includes(countryCode)) {
        return v.bankInfos;
      }
      return undefined;
    },
  );

  if (currencyBankInfo) {
    const [currency, infos] = currencyBankInfo as any;
    return [currency as Currency, infos.bankInfos];
  }

  // 1. Billing是HK, 订单是USD
  if (activeCurrency === 'USD' && countryCode === 'HK') {
    return [
      activeCurrency,
      [
        {
          'Account name': 'WUKONG International (Hong Kong) Limited ',
          'Account number': '6300171581',
          'Bank Code': '007',
          'Branch Code': '630',
          'Swift Code': 'CHASHKHH',
          'Bank Name': 'JPMorgan Chase Bank N.A., Hong Kong Branch',
          'Bank Location': 'HONG KONG',
          'Account Type': 'Current',
          'Company address':
            'Room 2609, China Resource Building, 26 Harbour Road, Wanchai, Hong Kong',
        },
        {},
      ],
    ];
  }
  // 2. Billing是HKD
  if (activeCurrency === 'HKD' && countryCode === 'HK') {
    return [
      activeCurrency,
      [
        {
          'Account name': 'WUKONG International (Hong Kong) Limited ',
          'Account number': '6300171599',
          'Bank Code': '007',
          'Branch Code': '630',
          'Swift Code': 'CHASHKHH',
          'Bank Name': 'JPMorgan Chase Bank N.A., Hong Kong Branch',
          'Bank Location': 'HONG KONG',
          'Account Type': 'Current',
          'Company address':
            'Room 2609, China Resource Building, 26 Harbour Road, Wanchai, Hong Kong',
        },
        {},
      ],
    ];
  }
  // 3. 其他情况
  return [
    activeCurrency,
    [
      {
        'Account Name': 'SMART LEARNER INTERNATIONAL CORPORATION',
        'Company address/payee’s address':
          '340 E Middlefield Rd, Mountain View, CA 94043',
        'Account number': '922521593',
        'Routing number（direct deposits and ACH transactions）': '322271627',
        'Routing number（wire transfers）': '021000021',
        'Swift Code': 'CHASUS33',
        'Account location': 'United States',
        'Account type': 'Checking',
        'Bank name': 'JPMorgan Chase Bank, N.A.',
      },
      {},
    ],
  ];
};

/**
 * 获取atome可用币种
 * @param amount 金额
 * @returns 可用币种
 */
export const getAtomeUseAbleCurrency = async (
  amount: Money,
): Promise<ExchangeRate[]> => {
  const exchangeAmounts = await exchangeRateMultiple(
    ATOME_USEABLE_CURRENCIES.map((c: any) => {
      return {
        money: amount,
        target: c.currency,
      };
    }),
  );
  return exchangeAmounts.filter(a => {
    return ATOME_USEABLE_CURRENCIES.find((c: any) => {
      return c.currency === a.target.currency && c.limit.withIn(a.target.value);
    });
  });
};

/**
 * 支付重置接口
 * @param uuid 订单
 * @returns
 */
export const handlePaymentReset = (uuid: string): Promise<void> => {
  return http(`public/api/payment/${uuid}/reset`, {
    method: 'PUT',
    toJson: false,
  });
};

/**
 * 获取同意条款链接
 * @param id 订单id
 * @returns 链接url
 */
export const getAgreementInfo = async (id: string): Promise<string> => {
  try {
    const res =
      (await http(`public/api/ec/order/${id}/agreement`, {
        method: 'GET',
        toJson: false,
      })) || '""';
    return JSON.parse(res as string);
  } catch (error) {
    console.log(error);
  }
  return '';
};

/**
 * 根据ip地址获取当前地址信息
 */

export const getAddress = (): Promise<AddressFromIpRes> => {
  return http('public/ip/ipInfoAGGCms', {
    method: 'GET',
  });
};

/**
 * 查询国家列表
 */

export const getCountry = async (): Promise<Array<AddressListRes>> => {
  const data = (await http(`public/world_city?language=EN`, {
    method: 'GET',
  })) as any;
  return (
    data?.filter((item: any) => {
      return item.iso2 !== 'CN';
    }) || []
  );
};

/**
 * 根据国家名称查询州省列表
 * @param {String} country 国家名称
 */

export const getStates = (country: string): Promise<Array<AddressListRes>> => {
  return http(`public/world_city?language=EN&country=${country}`, {
    method: 'GET',
  });
};

/**
 * 根据国家和州省查询城市列表
 * @param {String} country 国家
 * @param {String} state 州省
 */

export const getCitys = (
  country: string,
  state: string,
): Promise<Array<AddressListRes>> => {
  return http(
    `public/world_city?language=EN&country=${country}&state=${state}`,
    {
      method: 'GET',
    },
  );
};

/**
 * 提交账单信息
 * @param {String} data 账单信息
 */

export const submitpaymentInfo = (data: BillingInfoPayload): Promise<void> => {
  return http(`public/api/ec/order/billingInfo`, {
    method: 'put',
    data,
  });
};

/**
 * 对照账单地域信息
 * @param {String} studentNumber 学号
 * @param {String} orderId 学号
 */

export const judgeBillingInfo = (
  studentNumber: string,
  orderId: string,
): Promise<{ key: boolean; value: string }> => {
  return http(
    `public/api/ec/order/billingInfo/fillLatest/${orderId}/${studentNumber}`,
    {
      method: 'get',
    },
  );
};

/**
 * 创建订阅
 * @returns
 */
export const createSubscription = (
  data: SubscriptionOptions,
  version?: SubscribeVersion,
) => {
  if (version === 'v3') {
    return client.post(`/api/student/order/subscribe3/create`, data);
  }
  return client.post(`/api/student/order/subscribe/create`, data);
};

/**
 * 检查用户邮箱
 * @returns
 */
export const checkEmail = () => {
  return client.get(`/api/student/order/email/check`);
};

/**
 * 获取可用代金劵
 */
export const getUnusedCoupon = (data: {
  studentCode: string;
  currency: Currency;
}) => {
  return client.post(`/api/student/order/coupon/unused`, data);
};

/**
 * 3.12 家庭绑定邮箱
 */
export const emailSubmit = (data: { email: string }): Promise<boolean> => {
  return client.post(`/api/student/order/family/email/submit`, data);
};

export const getStudentList = () => {
  return client.get(`/api/acc/account/students`);
};
/**
 * 3.7 获取所有价格
 *
 * @returns
 */
export const getSubscribePrice = (
  data: SubscribePricePayload,
  version?: SubscribeVersion,
) => {
  if (version === 'v3') {
    return client.post(`/api/student/order/subscribe3/price`, data);
  }
  return client.post(
    `/api/student/order/subscribe/useCoupon/actualPaymentAmount`,
    data,
  );
};

/**
 * 3.18 支付界面获取订阅订单详情
 *
 * @returns
 */
export const getDetailForPayment = (
  subscribeId: string,
  version?: SubscribeVersion,
  noAuth?: boolean,
) => {
  // TODO: 无鉴权接口
  if (noAuth) {
    return Promise.resolve({} as OrderRespDto);
  }
  if (version === 'v3') {
    return client.get(
      `/api/student/order/subscribe3/detailForPayment/${subscribeId}`,
    );
  }
  return client.get(
    `/api/student/order/subscribe/detailForPayment/${subscribeId}`,
  );
};

/**
 * 3.7 获取所有价格
 *
 * @returns
 */
export const actualPaymentAmount = (
  data: SubscribePricePayload & { couponId: string },
  version?: SubscribeVersion,
) => {
  if (version === 'v3') {
    return client.post(`/api/student/order/subscribe3/useCoupon/price`, data);
  }
  return client.post(`/api/student/order/subscribe/actualPaymentAmount`, data);
};

/**
 * 用过paymentId获取订单信息
 * @param uuid 订单主键
 * @param paymenyId 支付id
 * @returns 订单信息
 */
export const getSubscriptionByPaymenyId = (data: {
  subscribeId: string;
  orderId: string;
  paymentId: string;
  noAuth?: boolean;
}): Promise<OrderRespDto> => {
  if (data?.noAuth) {
    // TODO: 无鉴权接口
    return Promise.resolve({} as OrderRespDto);
  }
  return client.post(`/api/student/order/subscribe/find/payment`, data);
};

export const getMergeOrderData = (data: {
  subscribeId: string;
  orderId: string;
  paymentId: string;
  orderType: OrderType;
  noAuth?: boolean;
}) => {
  if (data?.orderType === OrderType.SUBSCRIPTION) {
    return getSubscriptionByPaymenyId(data);
  }

  return getOrderByPaymenyId(data?.orderId, data?.paymentId, data?.noAuth);
};
/**
 * 同步支付状态
 * 
 * @param data orderId: string;
  paymentId: string;
 * @returns 
 */
export const refreshPaymentStatus = (data: {
  orderId: string;
  paymentId: string;
}) => {
  return client.post<
    RefreshPaymentStatusResponse,
    RefreshPaymentStatusResponse
  >(`/api/student/order/sysn/channel/payment/status`, data);
};
/**
 * 订阅订单
 * @param uuid
 */
export const getSubscriptionOrderInfoV2 = async (
  uuid: string,
): Promise<OrderProps> => {
  const res = (await client.get(
    `/public/open/subscribe/${uuid}/deduction`,
  )) as OrderProps;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  res.money = res?.actualPaymentAmount;
  return res;
};

/**
 * 获取billing信息
 *
 */
export const getBillingInfo = (id: string): Promise<any> => {
  return http(`public/api/subscription/${id}/billing`, {
    method: 'get',
  });
};

/**
 * order summary
 */
export const getOrderSummary = (id: string, noAuth?: boolean): Promise<any> => {
  // TODO: 无鉴权接口
  if (noAuth) {
    return http(`public/api/ec/order/cash_register/${id}`, {
      method: 'get',
    });
  }
  return client.get(`/api/student/order/detail/cash_register/${id}`);
};

/**
 * 订阅3.0获取意向老师数据
 */
export const getIntentionTeacher = (studentCode: string) => {
  return client.get(
    `/public/self/schedule/subscribe3/${studentCode}/intentionTeacher`,
  );
};

/**
 * 查询学号关联的学生是否为ES用户
 */
export const getIsEsUser = async (studentCode: string) => {
  if (!studentCode) {
    return '';
  }
  const result = (await client.get(
    `/api/student/master/v2/isEsUser/${studentCode}`,
  )) as any;
  return result?.content ? 'ES' : ('CS' as keyof typeof filterKeysMap);
};
/**
 * 根据订单获取或创建自主约课单
 *
 * @param orderNumber
 * @returns
 */
export const getPaymentCheckBooking = (orderNumber: string): Promise<any> => {
  return client.get(`public/open/self-booking/find/${orderNumber}`);
};

/**
 * 获取地址补全列表
 */
export const getPaymentAddressList = async (data: {
  street: string;
  countryCode: CountryCode;
}) => {
  return await client.post<any, any>(
    `${WK_GATEWAY_URL}/ms-cms/public/address/completion/suggestions`,
    data,
  );
};

/**
 * 获取地址详情
 */
export const getPaymentAddressDetail = async (placeId: string) => {
  return await client.get<any, any>(
    `${WK_GATEWAY_URL}/ms-cms/public/address/completion/detail/${placeId}`,
  );
};
